import { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useAnnotateRateDiscrepancyMutation,
  useGetRateCheckQuery,
  useLazyExportRateCheckXlsxQuery,
  useListRateDiscrepanciesQuery,
} from '@shared/api';
import type { RateDiscrepancy } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconArrowLeft, IconDownload } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';

const SEVERITIES: Array<{ value: '' | RateDiscrepancy['severity']; label: string }> = [
  { value: 'error', label: 'Только ошибки' },
  { value: 'warning', label: 'Warnings' },
  { value: 'info', label: 'Info' },
  { value: '', label: 'Все' },
];

const DECISIONS: Array<{ value: '' | RateDiscrepancy['decision']; label: string }> = [
  { value: 'pending', label: 'Без решения' },
  { value: 'accepted', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'disputed', label: 'Disputed' },
  { value: '', label: 'Все' },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  width: max-content;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Counters = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Counter = styled.div<{ $tone?: 'danger' | 'warning' | 'info' }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  min-width: 140px;

  & .num {
    font-size: 22px;
    font-weight: 700;
    color: ${({ theme, $tone }) => {
      if ($tone === 'danger') return theme.colors.status.danger;
      if ($tone === 'warning') return theme.colors.status.warning;
      return theme.colors.text.primary;
    }};
  }
  & .label {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.muted};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const FilterRow = styled.div<{ $mt?: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const FilterLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: ${({ theme }) => theme.spacing(2)};
`;

const FilterBtn = styled.button<{ $active?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : 'transparent'};
  color: inherit;
  font: inherit;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const Note = styled.div<{ $mt?: number; $danger?: boolean }>`
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.status.danger : theme.colors.text.muted};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const DiscRow = styled(Card)<{ $severity: RateDiscrepancy['severity'] }>`
  border-left: 4px solid
    ${({ theme, $severity }) =>
      $severity === 'error'
        ? theme.colors.status.danger
        : $severity === 'warning'
          ? theme.colors.status.warning
          : theme.colors.border};
`;

const DiscHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const KindPill = styled.span`
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
`;

const Message = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

// Stage F2: блок LLM-рекомендации по замене расценки.
const Recommendation = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;

  & .ico {
    flex-shrink: 0;
  }
`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ValueBlock = styled.div`
  font-size: 12.5px;
  & .lbl {
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.colors.text.muted};
    margin-bottom: ${({ theme }) => theme.spacing(1)};
  }
  & pre {
    margin: 0;
    padding: ${({ theme }) => theme.spacing(2)};
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.radius.sm};
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 11.5px;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

// Stage F1: читаемая таблица семантических кандидатов вместо сырого JSON.
const CandTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  & th,
  & td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  & th {
    color: ${({ theme }) => theme.colors.text.muted};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.06em;
  }
  & td.code {
    font-family: ${({ theme }) => theme.font.mono};
    white-space: nowrap;
  }
  & td.sim {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const HitlBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const CommentArea = styled.textarea`
  flex: 1;
  min-width: 240px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font: inherit;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;
  min-height: 36px;
`;

const DecisionBadge = styled(Badge)`
  margin-left: auto;
`;

function severityTone(severity: RateDiscrepancy['severity']): 'danger' | 'warning' | 'info' {
  if (severity === 'error') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function decisionTone(
  decision: RateDiscrepancy['decision'],
): 'neutral' | 'success' | 'danger' | 'warning' {
  if (decision === 'accepted') return 'success';
  if (decision === 'rejected') return 'danger';
  if (decision === 'disputed') return 'warning';
  return 'neutral';
}

/** Вид находки словами; код — в подсказке. Неизвестный вид — как есть. */
const RATE_KIND_LABELS: Record<string, string> = {
  not_found: 'Шифр не найден',
  unknown_method: 'Метод не распознан',
  manual_review: 'Проверить вручную',
  price_mismatch: 'Цена расходится',
  price_unindexed: 'Цена без индекса',
  price_unindexed_rim: 'Цена без индекса РИМ',
  resource_qty: 'Состав ресурсов',
  nr_mismatch: 'НР расходятся',
  sp_mismatch: 'СП расходятся',
  norm_missing: 'Нет норматива',
  semantic_candidates: 'Похожие расценки',
  index_reference: 'Индекс — справка',
  index_mismatch: 'Индекс расходится',
  coef_value_mismatch: 'Коэффициент: значение',
  coef_not_applicable: 'Коэффициент не к этой норме',
  coef_unknown: 'Коэффициент не найден',
  coef_no_code: 'Коэффициент без шифра ГГЭ',
  tg_outside_group: 'Ресурс вне технологической группы',
  tg_unresolved: 'Материал по проекту не выбран',
  uncached_formulas: 'Формулы без значений',
};

function rateKindLabel(kind: string): string {
  return RATE_KIND_LABELS[kind] ?? kind;
}

// Кандидат семантического матча (kind='semantic_candidates'), лежит в
// ref_value.candidates (см. rate_engine.rs::emit_semantic_candidates).
interface RateCandidate {
  code: string;
  name?: string;
  unit?: string | null;
  pz?: number | null;
  distance: number;
}

function extractCandidates(ref: Record<string, unknown>): RateCandidate[] | null {
  const arr = (ref as { candidates?: unknown }).candidates;
  if (!Array.isArray(arr)) return null;
  const out = arr.filter(
    (c): c is RateCandidate =>
      !!c && typeof c === 'object' && typeof (c as RateCandidate).code === 'string',
  );
  return out.length > 0 ? out : null;
}

function DiscrepancyCard({
  d,
  checkId,
  canWrite,
}: {
  d: RateDiscrepancy;
  checkId: string;
  canWrite: boolean;
}) {
  const [comment, setComment] = useState<string>(d.comment ?? '');
  const [annotate, { isLoading }] = useAnnotateRateDiscrepancyMutation();

  async function submit(decision: 'accepted' | 'rejected' | 'disputed') {
    try {
      await annotate({
        id: d.id,
        checkId,
        decision,
        comment: comment.trim() ? comment.trim() : undefined,
      }).unwrap();
    } catch (err) {
      window.alert(apiErrorText(err, 'Не удалось сохранить разметку.'));
    }
  }

  return (
    <DiscRow $severity={d.severity}>
      <DiscHeader>
        <Badge $tone={severityTone(d.severity)}>{d.severity}</Badge>
        <KindPill title={d.kind}>{rateKindLabel(d.kind)}</KindPill>
        <DecisionBadge $tone={decisionTone(d.decision)}>{d.decision}</DecisionBadge>
      </DiscHeader>
      <Message>{d.message}</Message>
      {d.recommendation && (
        <Recommendation>
          <span className="ico">💡</span>
          <span>{d.recommendation}</span>
        </Recommendation>
      )}
      <ValueGrid>
        <ValueBlock>
          <div className="lbl">Заявлено подрядчиком</div>
          <pre>{JSON.stringify(d.applied_value, null, 2)}</pre>
        </ValueBlock>
        <ValueBlock>
          <div className="lbl">
            {d.kind === 'semantic_candidates' ? 'Похожие расценки' : 'Норматив'}
          </div>
          {(() => {
            const cands =
              d.kind === 'semantic_candidates' ? extractCandidates(d.ref_value) : null;
            if (!cands) {
              return <pre>{JSON.stringify(d.ref_value, null, 2)}</pre>;
            }
            return (
              <CandTable>
                <thead>
                  <tr>
                    <th>Шифр</th>
                    <th>Наименование</th>
                    <th>Сходство</th>
                  </tr>
                </thead>
                <tbody>
                  {cands.map((c) => (
                    <tr key={c.code}>
                      <td className="code">{c.code}</td>
                      <td>{c.name ?? '—'}</td>
                      <td className="sim">
                        {Math.max(0, (1 - c.distance) * 100).toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CandTable>
            );
          })()}
        </ValueBlock>
      </ValueGrid>
      <HitlBar>
        <CommentArea
          placeholder="Комментарий (опционально)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={!canWrite}
        />
        <Button
          type="button"
          $variant="primary"
          onClick={() => submit('accepted')}
          disabled={!canWrite || isLoading || d.decision === 'accepted'}
        >
          Принять
        </Button>
        <Button
          type="button"
          $variant="ghost"
          onClick={() => submit('rejected')}
          disabled={!canWrite || isLoading || d.decision === 'rejected'}
        >
          Отклонить
        </Button>
        <Button
          type="button"
          $variant="ghost"
          onClick={() => submit('disputed')}
          disabled={!canWrite || isLoading || d.decision === 'disputed'}
        >
          Спорно
        </Button>
      </HitlBar>
    </DiscRow>
  );
}

const ExportBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

export function RateCheckDiscrepancies() {
  const { id } = useParams<{ id: string }>();
  const { can_write } = useAgentPermission('smeta');
  const [severity, setSeverity] = useState<'' | RateDiscrepancy['severity']>('error');
  const [decision, setDecision] = useState<'' | RateDiscrepancy['decision']>('');
  const [exportXlsx, { isFetching: exporting }] = useLazyExportRateCheckXlsxQuery();
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    if (!id) return;
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rate_check_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить отчёт XLSX.');
      console.error('exportRateCheckXlsx failed', err);
    }
  }

  // Поллим check — чтобы видеть прогресс анализа, если кто-то перезапустил.
  const { data: check } = useGetRateCheckQuery(id!, {
    skip: !id,
    pollingInterval: 3000,
  });
  const { data, isLoading, isError } = useListRateDiscrepanciesQuery(
    {
      checkId: id!,
      severity: severity || undefined,
      decision: decision || undefined,
    },
    { skip: !id },
  );

  if (!id) return null;

  const status = check?.check.status ?? 'unknown';
  const items = data?.items ?? [];
  const counts = data?.counts ?? { error: 0, warning: 0, info: 0 };

  return (
    <Wrap>
      <Back to={`/agents/smeta/rate-checks/${id}`}>
        <IconArrowLeft size={16} />
        <span>К проверке</span>
      </Back>

      <Card>
        <Counters>
          <Counter $tone="danger">
            <span className="num">{counts.error}</span>
            <span className="label">Errors</span>
          </Counter>
          <Counter $tone="warning">
            <span className="num">{counts.warning}</span>
            <span className="label">Warnings</span>
          </Counter>
          <Counter>
            <span className="num">{counts.info}</span>
            <span className="label">Info</span>
          </Counter>
          <Counter>
            <span className="num">{check?.check.position_count ?? '—'}</span>
            <span className="label">Позиций</span>
          </Counter>
        </Counters>
        {status === 'analyzed' && (
          <ExportBar>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={handleExport}
              disabled={exporting}
            >
              <IconDownload size={15} />
              {exporting ? 'Готовлю…' : 'Скачать XLSX'}
            </Button>
          </ExportBar>
        )}
        {exportError && (
          <Note $mt={8} $danger>{exportError}</Note>
        )}
        {status === 'analyzing' && (
          <Note $mt={12}>
            Анализ идёт… таблица обновляется автоматически каждые 3 сек.
          </Note>
        )}
      </Card>

      <Card>
        <FilterRow>
          <FilterLabel>Severity</FilterLabel>
          {SEVERITIES.map((s) => (
            <FilterBtn
              key={s.value || 'all-sev'}
              type="button"
              $active={severity === s.value}
              onClick={() => setSeverity(s.value)}
            >
              {s.label}
            </FilterBtn>
          ))}
        </FilterRow>
        <FilterRow $mt={12}>
          <FilterLabel>Decision</FilterLabel>
          {DECISIONS.map((d) => (
            <FilterBtn
              key={d.value || 'all-dec'}
              type="button"
              $active={decision === d.value}
              onClick={() => setDecision(d.value)}
            >
              {d.label}
            </FilterBtn>
          ))}
        </FilterRow>
      </Card>

      {isLoading && (
        <Card>
          <Note>Загрузка расхождений…</Note>
        </Card>
      )}
      {isError && (
        <Card>
          <Note>Не удалось загрузить расхождения.</Note>
        </Card>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <Card>
          <Note>
            {status === 'analyzed'
              ? 'Расхождений по выбранным фильтрам нет.'
              : 'Анализ ещё не запускался — вернитесь на страницу проверки и нажмите «Запустить анализ».'}
          </Note>
        </Card>
      )}
      {items.map((d) => (
        <DiscrepancyCard key={d.id} d={d} checkId={id} canWrite={can_write} />
      ))}
    </Wrap>
  );
}
